<template>
  <b-form @submit.prevent="onSubmit" enctype="multipart/form-data">
    <b-form-group
      id="input-group-title"
      label="File Name"
      label-for="file-name"
      class="font-weight-bold pr-2"
    >
      <b-form-input
        id="file-name"
        type="text"
        required
        :value="file.name"
        v-model="form.name"
        aria-describedby="file-name-feedback"
      ></b-form-input>

      <b-form-invalid-feedback
        id="input-title-feedback"
      ></b-form-invalid-feedback>
    </b-form-group>
    <b-container fluid>
      <b-row>
        <b-col
          v-for="selected in fields"
          :key="selected.id"
          cols="12"
          :sm="
            (selected.field_type === 'string' &&
              selected.label !== 'State of Issue' &&
              selected.field_type !== 'country') ||
            !hasSwitch ||
            selected.field_type === 'traffic_controller_qualifications'
              ? '12'
              : '6'
          "
          class="pr-2 mb-0"
        >
          <b-form-group
            v-if="selected.field_type === 'string'"
            :id="selected.label"
            :label="selected.label"
            class="font-weight-bold"
          >
            <b-form-input
              :id="selected.label"
              :value="selected.value"
              type="text"
              required
              @change="addInformation($event, selected.id)"
            ></b-form-input>

            <b-form-invalid-feedback id="input-title-feedback">
              <!-- eslint-disable-next-line -->
              <!-- {{ errors.title }} -->
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- date -->
          <div
            v-if="selected.field_type === 'date'"
            class="mb-3"
            :class="{ 'highlight-expiry': isHighlightedExpiryField(selected) }"
          >
            <label class="font-weight-bold">
              {{ selected.label }}
              <span
                v-if="isHighlightedExpiryField(selected)"
                class="highlight-expiry-badge"
                >Update this</span
              >
            </label>
            <b-form-datepicker
              class="datepicker"
              today-button
              reset-button
              no-flip
              placeholder="dd/mm/yyyy"
              aria-describedby="input-date-feedback"
              :show-decade-nav="true"
              :value="selected.value"
              :date-format-options="{
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              }"
              @input="onSelectedDate($event, selected)"
            />
            <div class="invalid-feedback d-block">
              {{ errors.date.field === selected.id ? errors.date.message : '' }}
            </div>
            <!-- prettier-ignore -->
            <b-form-group v-if="hasSwitch && hasSwitch.base_information_type_id === selected.id" class="pt-2">
              <b-form-checkbox switch size="lg" v-model="calculate" @input="calculateExpiryDate()"><label
                  class="font-weight-bold pl-3 custom-label">
                  Automatically calculate {{ calculateYears }} years from expiry
                  date
                </label></b-form-checkbox>
            </b-form-group>
          </div>

          <!-- country -->
          <div v-if="selected.field_type === 'country'" class="mb-3">
            <label for="country" class="font-weight-bold">{{
              selected.label
            }}</label>
            <b-form-select
              required
              :id="selected.label"
              :value="selected.value"
              value-field="name"
              text-field="name"
              :options="countries"
              @change="addInformation($event, selected.id)"
            ></b-form-select>
          </div>

          <!-- classes -->
          <div v-if="selected.field_type === 'classes'" class="mb-3">
            <label for="classes" class="font-weight-bold">
              {{ selected.label }}
            </label>

            <!-- prettier-ignore -->
            <b-form-input v-model="classes" id="input-classes" type="text" required class="bg-white"
              @focus="onPickingOption('high_risk_work_class', true); $event.target.blur()" />

            <!-- prettier-ignore -->
            <OptionsPickerModal :showModal="isPickingClass" optionType="high_risk_work_class" :existingOptions="classes"
              @onCloseOptionsPickerModal="onPickingOption('high_risk_work_class', false)"
              @onOptionsPicked="(options) => onOptionsPicked('high_risk_work_class', options, selected.id)" />
          </div>

          <!-- qualifications -->
          <div
            v-if="selected.field_type === 'traffic_controller_qualifications'"
            class="mb-3"
          >
            <label for="qualifications" class="font-weight-bold">
              {{ selected.label }}
            </label>

            <!-- prettier-ignore -->
            <b-form-input v-model="qualifications" id="input-qualifications" type="text" required class="bg-white"
              @focus="onPickingOption('traffic_controller_qualification', true); $event.target.blur()" />

            <!-- prettier-ignore -->
            <OptionsPickerModal :showModal="isPickingQualification" optionType="traffic_controller_qualification"
              :existingOptions="qualifications"
              @onCloseOptionsPickerModal="onPickingOption('traffic_controller_qualification', false)"
              @onOptionsPicked="(options) => onOptionsPicked('traffic_controller_qualification', options, selected.id)" />
          </div>

          <!-- driver licence classes -->
          <div
            v-if="selected.field_type === 'driver_licence_classes'"
            class="mb-3"
          >
            <label class="font-weight-bold">
              {{ selected.label }}
            </label>

            <!-- prettier-ignore -->
            <b-form-input v-model="driverLicenceClasses" id="input-driver-licence-classes" type="text" required
              class="bg-white" @focus="onPickingOption('driver_licence_class', true); $event.target.blur()" />

            <!-- prettier-ignore -->
            <OptionsPickerModal :showModal="isPickingDriverLicenceClasses" optionType="driver_licence_class"
              :existingOptions="driverLicenceClasses"
              @onCloseOptionsPickerModal="onPickingOption('driver_licence_class', false)"
              @onOptionsPicked="(options) => onOptionsPicked('driver_licence_class', options, selected.id)" />
          </div>

          <!-- state -->
          <div v-if="selected.field_type === 'state'" class="mb-3">
            <label for="state" class="font-weight-bold">
              {{ selected.label }}
            </label>

            <b-form-select
              required
              :options="visibleStates"
              :id="selected.label"
              :value="selected.value"
              value-field="value"
              text-field="value"
              placeholder="Select State"
              @change="onStateChanged($event, selected.id)"
            />
          </div>

          <!-- electrical licence type (needs a State field to depend on) -->
          <div
            v-if="
              selected.field_type === 'electrical_licence_type' && hasStateField
            "
            class="mb-3"
          >
            <label for="electrical-licence-type" class="font-weight-bold">
              {{ selected.label }}
            </label>

            <b-form-select
              id="electrical-licence-type"
              v-model="electricalTypeValue"
              required
              :disabled="!electricalState || electricalTypesLoading"
              :options="electricalTypeSelectOptions"
              @change="addInformation($event, selected.id)"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>{{
                  electricalTypePlaceholder
                }}</b-form-select-option>
              </template>
            </b-form-select>

            <div
              v-if="electricalTypesError"
              id="electrical-licence-type-error"
              class="text-danger small mt-1"
            >
              Couldn't load licence types.
              <b-button
                id="electrical-licence-type-retry"
                variant="link"
                size="sm"
                class="p-0 align-baseline"
                @click="loadElectricalTypes(electricalState)"
                >Retry</b-button
              >
            </div>
          </div>

          <!-- electrical restrictions / conditions -->
          <div
            v-if="selected.field_type === 'electrical_restrictions'"
            class="mb-3"
          >
            <label for="input-electrical-restrictions" class="font-weight-bold">
              {{ selected.label }}
            </label>

            <!-- prettier-ignore -->
            <b-form-input v-model="electricalRestrictions" id="input-electrical-restrictions" type="text" required
              class="bg-white" @focus="onPickingOption('electrical_restriction', true); $event.target.blur()" />

            <div
              id="electrical-restrictions-counter"
              class="small mt-1"
              :class="
                electricalRestrictionsTooLong ? 'text-danger' : 'text-muted'
              "
            >
              {{ electricalRestrictions.length }}/{{
                ELECTRICAL_RESTRICTIONS_MAX
              }}
              <span v-if="electricalRestrictionsTooLong">
                - {{ ELECTRICAL_RESTRICTIONS_MESSAGE }}
              </span>
            </div>

            <!-- prettier-ignore -->
            <OptionsPickerModal :showModal="isPickingElectricalRestrictions" optionType="electrical_restriction"
              :existingOptions="electricalRestrictions"
              :maxLength="ELECTRICAL_RESTRICTIONS_MAX"
              @onCloseOptionsPickerModal="onPickingOption('electrical_restriction', false)"
              @onOptionsPicked="(options) => onOptionsPicked('electrical_restriction', options, selected.id)" />
          </div>

          <!-- electrical conditions (exact wording), optional -->
          <b-form-group
            v-if="selected.field_type === 'electrical_conditions'"
            id="electrical-conditions-group"
            label-for="electrical-conditions"
            :label="selected.label"
            class="font-weight-bold"
          >
            <b-form-input
              id="electrical-conditions"
              :value="selected.value"
              type="text"
              maxlength="255"
              @change="addInformation($event, selected.id)"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-container>

    <b-container v-if="showFileVisibilityControl" class="pr-2 pl-0">
      <div class="font-weight-bold mb-2">File Visibility</div>
      <div class="visibility-card mb-3">
        <div class="visibility-toggle-wrap">
          <b-form-radio-group
            v-model="form.visibility"
            :options="visibilityOptions"
            buttons
            button-variant="outline-primary"
            name="file-visibility"
            class="visibility-toggle"
          />
        </div>

        <div class="visibility-help mt-2">
          <span class="visibility-mode-badge mr-2">{{
            visibilityModeLabel
          }}</span>
          <div class="visibility-help-title mb-0">
            <template v-if="isVisibilityLocked">
              <span>
                You can't change this file to Private while you're
                {{ visibilityLockReason || 'associated with a company' }}. End
                the association to change its visibility.
              </span>
            </template>
            <template
              v-else-if="
                isBusinessAdminUploadingForTradie &&
                form.visibility === 'public'
              "
            >
              <span>
                Visible to the Tradie and may be visible to other companies
                linked to that Tradie.
              </span>
            </template>
            <template
              v-else-if="
                isBusinessAdminUploadingForTradie &&
                form.visibility === 'private'
              "
            >
              <span>
                Only visible to your company and not shared with the Tradie or
                other linked companies. For company-only documents, use
                <strong>Private</strong> or upload them via the
                <strong>Company Files</strong> section.
              </span>
            </template>
            <template v-else>
              {{ visibilityHelpText }}
            </template>
          </div>
        </div>
      </div>
    </b-container>

    <b-container v-if="isRequestFileCreation" class="pr-2 pl-0">
      <div class="visibility-help mb-3 font-italic text-muted">
        This file will be shared as <strong>Public</strong> so the company that
        requested it can see it.
      </div>
    </b-container>

    <b-container class="pr-2 pl-0">
      <div class="page-subtitle mt-5 mb-4">File Upload</div>
      <template v-if="!requiresBack">
        <div
          class="upload-container dashed mb-4 upload-border"
          v-cloak
          @drop.prevent="loadPhotos($event)"
          @dragover.prevent
        >
          <b-img
            :src="require('@/assets/icons/upload.svg')"
            alt="Photos"
            class="mt-4"
            v-bind="mainProps"
          ></b-img>

          <div class="upload-label mt-3 mb-3">
            <b-button variant="primary" @click="launchFilePicker"
              >Upload from device</b-button
            >
          </div>

          <b-form-file
            ref="filePicker"
            multiple
            accept="image/jpeg, image/png, .pdf, .csv, .xlsx"
            style="display: none"
            @input="loadPhotos($event)"
            :state="state.files"
            aria-invalid="input-files-feedback"
          ></b-form-file>

          <label variant="secondary">Or drag them in</label>
        </div>

        <div class="preview-container" v-if="previews.length !== 0">
          <div
            class="preview-item"
            :class="pIndex !== previews.length - 1 ? 'mr-3' : ''"
            v-for="(preview, pIndex) in previews"
            :key="pIndex"
          >
            <b-icon-x-circle-fill
              variant="dark"
              class="remove-icon"
              @click="onRemovePreview(pIndex)"
            ></b-icon-x-circle-fill>

            <!-- previewProps carries `blank: true`, which makes b-img/b-img-lazy
               discard `src` entirely and render a generated placeholder
               instead — correct for the lazy-loading photo thumbnails it was
               written for (blank until scrolled into view, then swapped),
               but it silently blanks out the pdf/csv/xlsx bundled icons too
               since those never go through a lazy swap. Icons get a plain
               b-img with no previewProps so their `src` is actually
               respected. -->
            <b-img
              v-if="
                preview === 'pdf' || preview === 'csv' || preview === 'xlsx'
              "
              :src="
                preview === 'pdf'
                  ? require('@/assets/icons/adobe-acrobat.svg')
                  : preview === 'csv'
                  ? require('@/assets/icons/csv-file.svg')
                  : require('@/assets/icons/xlsx-file.svg')
              "
              alt="Photos"
              center
              class="h-100"
            ></b-img>
            <b-img-lazy
              v-else
              :src="preview"
              alt="Photos"
              v-bind="previewProps"
              class="h-100"
            ></b-img-lazy>
          </div>
        </div>
      </template>

      <!-- Card templates (requires_back): one image slot per side, so the
           upload order is always [front, back]. -->
      <b-row v-else class="mb-4" data-testid="front-back-slots">
        <b-col v-for="side in cardSides" :key="side.key" cols="12" sm="6">
          <div class="font-weight-bold mb-2 text-center">{{ side.label }}</div>
          <div
            class="upload-container dashed upload-border card-slot"
            :class="{ 'is-invalid': slotErrors[side.key] }"
            :data-testid="`slot-${side.key}`"
            @drop.prevent="onSlotPicked(side.key, $event)"
            @dragover.prevent
          >
            <template v-if="slotPreviews[side.key]">
              <b-icon-x-circle-fill
                variant="dark"
                class="remove-icon"
                :data-testid="`slot-${side.key}-remove`"
                @click="onRemoveSlot(side.key)"
              ></b-icon-x-circle-fill>
              <b-img
                :src="slotPreviews[side.key]"
                :alt="`${side.label} of card`"
                class="slot-preview"
              ></b-img>
            </template>
            <b-img
              v-else
              :src="require('@/assets/icons/upload.svg')"
              alt="Photos"
              class="mt-4"
              v-bind="mainProps"
            ></b-img>
            <div class="upload-label mt-3 mb-3">
              <b-button
                variant="primary"
                :data-testid="`slot-${side.key}-pick`"
                @click="launchSlotPicker(side.key)"
                >{{
                  slotPreviews[side.key] ? 'Replace' : `Upload ${side.label}`
                }}</b-button
              >
            </div>
            <input
              :ref="`slotInput-${side.key}`"
              type="file"
              accept="image/jpeg, image/png"
              style="display: none"
              :data-testid="`slot-${side.key}-input`"
              @change="onSlotPicked(side.key, $event)"
            />
          </div>
          <div
            v-if="slotErrors[side.key]"
            class="invalid-feedback d-block text-center"
            :data-testid="`slot-${side.key}-error`"
          >
            {{ slotErrors[side.key] }}
          </div>
        </b-col>
      </b-row>

      <b-container v-if="showPrivacyConsent" class="pr-2 pl-0">
        <div>
          <b-checkbox
            v-model="filePrivacyConsent"
            :value="true"
            :unchecked-value="false"
          >
            By ticking this box, you confirm that you have read and understood
            the
            <a
              href="https://cms.tradiepass.com.au/support/privacy"
              target="_blank"
            >
              Privacy Policy
            </a>
            and
            <a
              href="https://cms.tradiepass.com.au/support/privacy-collection"
              target="_blank"
            >
              Privacy Collection Notice
            </a>
            . If you are a company uploading information on behalf of an
            individual, you warrant that you have the consent of the individual
            to share this information with us, and for us to use it in
            accordance with our Terms and Conditions, Privacy Policy and Privacy
            Collection Notice.
          </b-checkbox>
        </div>
      </b-container>

      <b-container v-if="showManualUploadConsent" class="pr-2 pl-0 mt-3">
        <div class="consent-box p-3">
          <div class="font-weight-bold mb-1">
            <b-icon icon="shield-lock" class="mr-1" />
            Privacy Collection Notice
          </div>
          <b-link
            class="small"
            @click="manualConsentNoticeOpen = !manualConsentNoticeOpen"
          >
            {{ manualConsentNoticeOpen ? 'Hide' : 'Read' }} the Privacy
            Collection Notice
          </b-link>
          <b-collapse v-model="manualConsentNoticeOpen" class="mt-2">
            <p class="small text-muted consent-notice-text">
              {{ privacyCollectionNotice }}
            </p>
          </b-collapse>
          <b-checkbox
            v-model="manualUploadConsent"
            :value="true"
            :unchecked-value="false"
            class="mt-2"
          >
            By ticking this box, you confirm that you have read and understood
            the
            <a :href="consentUrls.privacy" target="_blank" rel="noopener">
              Privacy Policy
            </a>
            and
            <a
              :href="consentUrls.privacyCollection"
              target="_blank"
              rel="noopener"
            >
              Privacy Collection Notice
            </a>
            . If you are a company uploading information on behalf of an
            individual, you warrant that you have the consent of the individual
            to share this information with us, and for us to use it in
            accordance with our Terms and Conditions, Privacy Policy and Privacy
            Collection Notice. I confirm that I am authorised to provide the
            personal details presented and I consent to my information being
            checked with the document issuer or official record holder via third
            party systems for the purpose of confirming my identity.
          </b-checkbox>
        </div>
      </b-container>

      <div class="p-2 input-files-feedback" v-show="state.files === false">
        {{ errors.files }}
      </div>

      <b-row class="mt-5">
        <b-col>
          <b-button
            v-if="backTo"
            block
            class="btn-outlined bg-white text-dark"
            variant="outline-secondary"
            :to="backTo"
            >Back</b-button
          >
          <b-button
            v-else
            block
            class="btn-outlined bg-white text-dark"
            variant="outline-secondary"
            @click="onBack"
            >Back</b-button
          >
        </b-col>
        <b-col>
          <b-button
            block
            type="submit"
            variant="primary"
            :disabled="
              loading ||
              hasMissingRequiredDates ||
              !hasTickedPrivacyConsent ||
              !hasTickedManualUploadConsent
            "
          >
            <b-spinner v-if="loading" small label="Loading" />
            <div v-else>{{ isRequestFileCreation ? 'Submit' : 'Next' }}</div>
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-alert
      class="custom-alert"
      :show="dismissCountDown"
      :class="{
        'bg-danger border-danger': alertType === 'danger',
      }"
      dismissible
      fade
      :variant="alertType"
      @dismiss-count-down="countDownChanged"
    >
      {{ alertMsg }}
    </b-alert>
  </b-form>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { findIndex, find } from 'lodash'
import Compressor from 'compressorjs'
import dayjs from 'dayjs'
import OptionsPickerModal from '../modals/OptionsPickerModal.vue'
import { CONSENT_URLS, PRIVACY_COLLECTION_NOTICE } from '@/constants/consent'

// Per-image size cap (mirrors the backend's app/Rules/ImageOrPdfFile.php).
const IMAGE_MAX_BYTES = 30 * 1024 * 1024

export default {
  components: {
    OptionsPickerModal,
  },

  data() {
    return {
      loading: false,
      mainProps: {
        center: true,
        width: 80,
      },
      previewProps: {
        center: true,
        blank: true,
        blankColor: '#bbb',
      },
      previews: [],
      form: {
        id: null,
        name: this.file.name,
        visibility: 'public',
        files: [],
        information_types: [],
      },
      deletedFiles: [],
      format: 'dd/MM/yy',
      uploadType: 'Multiple Files',
      cardSides: [
        { key: 'front', label: 'Front' },
        { key: 'back', label: 'Back' },
      ],
      slotFiles: { front: null, back: null },
      slotPreviews: { front: null, back: null },
      slotErrors: { front: '', back: '' },
      // Bumped per pick so a slow read can't overwrite a newer choice.
      slotTokens: { front: 0, back: 0 },
      selectedDate: null,
      addedDate: null,
      calculate: false,
      state: {
        files: null,
      },
      errors: {
        files: null,
        date: {
          field: null,
          message: 'Please input a valid date',
        },
      },
      dismissCountDown: 0,
      alertMsg: '',
      alertType: 'primary',
      isPickingClass: false,
      classes: '',
      isPickingQualification: false,
      qualifications: '',
      isPickingDriverLicenceClasses: false,
      driverLicenceClasses: '',
      states: [],
      electricalState: '',
      electricalTypeValue: null,
      electricalTypeOptions: [],
      electricalTypesLoading: false,
      electricalTypesError: false,
      // Values saved on the file being edited, so they can be kept as a
      // selectable option if the list no longer contains them.
      savedElectricalState: '',
      savedElectricalType: null,
      electricalRestrictions: '',
      ELECTRICAL_RESTRICTIONS_MAX: 255,
      ELECTRICAL_RESTRICTIONS_MESSAGE:
        'Too many selections. Remove some (max 255 characters)',
      isPickingElectricalRestrictions: false,
      // Licence types already fetched, keyed by lowercase state code.
      electricalTypesCache: {},
      filePrivacyConsent: false,
      consentUrls: CONSENT_URLS,
      privacyCollectionNotice: PRIVACY_COLLECTION_NOTICE,
      // Manual-upload consent for a tradie uploading their own file — mirrors
      // the app's FileForm consent section. Only shown until it's been accepted.
      manualUploadConsent: false,
      manualConsentNoticeOpen: false,
    }
  },

  props: {
    file: {
      type: Object,
      required: true,
    },
    template: {
      type: Number,
      default: null,
    },
    // Templates whose card has details on both sides (template.requires_back).
    // Only set for new uploads, so editing an older single-image file is
    // unaffected.
    requiresBack: {
      type: Boolean,
      default: false,
    },
    selectedFields: {
      type: Array,
      default: null,
    },
    new: {
      type: Boolean,
      default: true,
    },
    hasSwitch: {
      type: Object,
      default: null,
    },
    isRequestFileCreation: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Object,
      default: null,
    },
    backTo: {
      type: [Object, String],
      default: null,
    },
    // Set by EditFilePage when this edit was opened from a "renewal
    // requested" notification — draws attention to the Expiry Date field,
    // since that's the one thing that actually needs updating.
    highlightExpiry: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      countries: (state) => state.country.countries,
      options: (state) => state.optionValue.list,
    }),
    fields() {
      if (this.selectedFields === null) {
        return this.file.file_information_types
      } else return this.selectedFields
    },

    hasElectricalType() {
      return this.fields.some(
        (field) => field.field_type === 'electrical_licence_type',
      )
    },

    hasStateField() {
      return this.fields.some((field) => field.field_type === 'state')
    },

    electricalRestrictionsTooLong() {
      return (
        this.electricalRestrictions.length > this.ELECTRICAL_RESTRICTIONS_MAX
      )
    },

    electricalTypePlaceholder() {
      if (!this.electricalState) return 'Select a state first'
      if (this.electricalTypesLoading) return 'Loading...'
      return 'Select licence type'
    },

    // A saved type that is no longer in the list is appended so it stays
    // selectable (and `required` is not silently unsatisfiable).
    electricalTypeSelectOptions() {
      const value = this.electricalTypeValue
      if (
        value &&
        !this.electricalTypesLoading &&
        !this.electricalTypeOptions.includes(value)
      ) {
        return [...this.electricalTypeOptions, value]
      }
      return this.electricalTypeOptions
    },

    // "International" has no electrical licence list, so it is not offered on
    // templates that have a licence type field.
    visibleStates() {
      if (!this.hasElectricalType) return this.states
      return this.states.filter((state) => state.value !== 'International')
    },

    limitUpload() {
      return (
        (this.uploadType === 'Single File' && this.form.files.length === 1) ||
        (this.uploadType === 'Multiple Files' && this.form.files.length === 2)
      )
    },

    isVisaOrPassport() {
      return this.file.name === 'Visa' || this.file.name === 'Passport'
    },

    calculateYears() {
      return this.hasSwitch.no_of_years
    },

    hasMissingRequiredDates() {
      return this.fields.some((field) => {
        if (field.field_type !== 'date') return false
        return this.isDateValueMissing(this.getDateFieldValue(field))
      })
    },

    hasTickedPrivacyConsent() {
      return !this.showPrivacyConsent || this.filePrivacyConsent
    },

    uploadingForTradieId() {
      const tradieId = localStorage.getItem('tradie_id')
      return tradieId === null || tradieId === 'undefined' ? null : tradieId
    },

    fileOwnerUserType() {
      if (!this.file) return null
      const u = this.file.user || (this.file.file && this.file.file.user)
      return (u && u.user_type) || null
    },

    fileOwnerUser() {
      if (!this.file) return null
      return this.file.user || (this.file.file && this.file.file.user) || null
    },

    isVisibilityLocked() {
      if (this.new) return false
      if (!this.file) return false
      if (this.file.can_change_visibility !== false) return false
      if (this.file.visibility !== 'public') return false
      if (!this.user || this.user.user_type !== 'tradie') return false
      const owner = this.fileOwnerUser
      return !!(owner && owner.mine)
    },

    visibilityLockReason() {
      if (!this.isVisibilityLocked) return ''
      return (this.file && this.file.visibility_lock_reason) || ''
    },

    /**
     * Business admin creating a file for their company only (not for a tradie).
     * Tradies and BAs in tradie context still get the Public/Private control.
     */
    isBusinessAdminNewUploadForOwnCompany() {
      return (
        this.new &&
        this.user &&
        this.user.user_type === 'business_admin' &&
        !this.uploadingForTradieId
      )
    },

    /**
     * Business admin editing a company file they (or another BA) own — same as new company upload, no Public/Private.
     * Files owned by a tradie (uploaded for a tradie) still show the control when allowed.
     */
    isBusinessAdminEditForOwnCompany() {
      if (this.new || !this.user || this.user.user_type !== 'business_admin') {
        return false
      }
      const ownerType = this.fileOwnerUserType
      if (ownerType === 'tradie') return false
      if (ownerType === 'business_admin') return true
      // When owner has no user_type, mirror new-upload routing (tradie_id in session = on-tradie flow).
      return !this.uploadingForTradieId
    },

    isBusinessAdminUploadingForTradie() {
      return (
        this.user &&
        this.user.user_type === 'business_admin' &&
        !!this.uploadingForTradieId
      )
    },

    visibilityOptions() {
      return [
        { text: 'Public', value: 'public' },
        {
          text: 'Private',
          value: 'private',
          disabled: this.isVisibilityLocked,
        },
      ]
    },
    visibilityHelpText() {
      const isTradie = this.user && this.user.user_type === 'tradie'

      if (isTradie) {
        return this.form.visibility === 'public'
          ? 'You and your linked companies can view this file, You can update this access at any time.'
          : 'This file is only visible to you, linked companies cannot see this, You can update this access at any time.'
      }

      if (this.isBusinessAdminUploadingForTradie) {
        return ''
      }

      return this.form.visibility === 'public'
        ? 'Visible to users allowed by current company sharing rules.'
        : 'Restricted based on current company sharing rules.'
    },
    visibilityModeLabel() {
      return this.form.visibility === 'public' ? 'Public' : 'Private'
    },

    showFileVisibilityControl() {
      // Answering a company's file request — visibility is forced to Public
      // (a Private file the requester can't see is a dead response), so the
      // toggle would only mislead. A note replaces it in the template.
      if (this.isRequestFileCreation) return false
      if (this.isBusinessAdminNewUploadForOwnCompany) return false
      if (this.isBusinessAdminEditForOwnCompany) return false
      if (this.new) return true
      if (!this.file) return false
      if (this.file.can_change_visibility === true) return true
      if (this.isVisibilityLocked) return true
      return false
    },

    showPrivacyConsent() {
      if (this.isBusinessAdminUploadingForTradie) return true
      return false
    },

    /**
     * Tradie uploading their own file — not a BA acting for a tradie, and not a
     * supervisor tradie uploading for someone else on their project (that flow
     * sets a different `tradie_id` in localStorage).
     */
    isTradieSelfUpload() {
      return (
        this.user &&
        this.user.user_type === 'tradie' &&
        !this.isBusinessAdminUploadingForTradie &&
        (!this.uploadingForTradieId ||
          String(this.uploadingForTradieId) === String(this.user.id))
      )
    },

    manualUploadConsentAlreadyGiven() {
      return !!(this.user && this.user.manual_upload_consent_given)
    },

    /**
     * Show the manual-upload consent notice + checkbox on a new upload by a
     * tradie who hasn't accepted it yet. Once accepted (here or in the app) it
     * never shows again.
     */
    showManualUploadConsent() {
      return (
        this.new &&
        this.isTradieSelfUpload &&
        !this.manualUploadConsentAlreadyGiven
      )
    },

    hasTickedManualUploadConsent() {
      return !this.showManualUploadConsent || this.manualUploadConsent
    },
  },

  watch: {
    'form.files'(files) {
      this.state.files = true
    },

    request(newVal, oldVal) {
      if (
        this.isRequestFileCreation &&
        newVal &&
        this.$route.name === 'request.request-file-new-page'
      ) {
        this.form.name = newVal.name
        this.form.information_types = newVal.information_types.map(
          (infoType) => infoType.id,
        )
      }
    },
  },

  methods: {
    ...mapActions({
      getCountries: 'country/getCountries',
      fileRequestShare: 'requests/fileRequestShare',
      getOptionValues: 'optionValue/getOptionValues',
      fetchOptionValues: 'optionValue/fetchOptionValues',
      acceptConsent: 'auth/acceptConsent',
    }),

    onBack() {
      this.$emit('back')
    },

    isHighlightedExpiryField(field) {
      return (
        this.highlightExpiry &&
        !!field.label &&
        field.label.toLowerCase().includes('expiry')
      )
    },

    getDateFieldValue(field) {
      if (this.new) {
        const entry = find(this.form.information_types, {
          information_type_id: field.id,
        })
        return entry ? entry.value : undefined
      }
      const entry = find(this.form.information_types, { id: field.id })
      return entry ? entry.value : undefined
    },

    isDateValueMissing(value) {
      if (value === null || value === undefined) return true
      if (typeof value === 'string' && value.trim() === '') return true
      return false
    },

    findFirstMissingDateFieldId() {
      for (const field of this.fields) {
        if (field.field_type !== 'date') continue
        if (this.isDateValueMissing(this.getDateFieldValue(field))) {
          return field.id
        }
      }
      return null
    },

    addInformation(event, id) {
      if (this.new) {
        if (find(this.form.information_types, { information_type_id: id })) {
          this.form.information_types.splice(
            findIndex(this.form.information_types, {
              information_type_id: id,
            }),
            1,
          )
        }
        this.form.information_types.push({
          information_type_id: id,
          value: event,
        })
      } else {
        this.updateInformation(event, id)
      }
    },

    onStateChanged(state, id) {
      this.addInformation(state, id)
      const typeField = find(this.fields, {
        field_type: 'electrical_licence_type',
      })
      if (!typeField) return
      this.electricalState = state
      this.electricalTypeValue = null
      this.electricalTypeOptions = []
      this.clearInformation(typeField.id)
      this.loadElectricalTypes(state)
    },

    clearInformation(id) {
      if (this.new) {
        const index = findIndex(this.form.information_types, {
          information_type_id: id,
        })
        if (index >= 0) this.form.information_types.splice(index, 1)
      } else {
        const index = findIndex(this.form.information_types, { id: id })
        if (index >= 0) this.form.information_types[index].value = ''
      }
    },

    isCurrentElectricalState(key) {
      return String(this.electricalState).toLowerCase() === key
    },

    async loadElectricalTypes(state) {
      this.electricalTypesError = false
      if (!state) {
        this.electricalTypeOptions = []
        this.electricalTypesLoading = false
        return
      }
      const key = String(state).toLowerCase()
      this.electricalTypeOptions = []
      this.electricalTypesLoading = true
      let values = this.electricalTypesCache[key]
      if (!values) {
        try {
          values = await this.fetchOptionValues(
            `electrical_licence_type_${key}`,
          )
        } catch (e) {
          // A failure for a state that is no longer selected is not ours to show.
          if (this.isCurrentElectricalState(key)) {
            this.electricalTypeOptions = []
            this.electricalTypesLoading = false
            this.electricalTypesError = true
          }
          return
        }
        this.electricalTypesCache[key] = values
      }
      // The state may have changed again while this request was in flight.
      if (!this.isCurrentElectricalState(key)) return
      this.electricalTypeOptions = values.map((option) => option.value)
      this.electricalTypesLoading = false

      const selected = this.electricalTypeValue
      if (selected && !this.electricalTypeOptions.includes(selected)) {
        const isSavedValue =
          !this.new &&
          selected === this.savedElectricalType &&
          String(this.savedElectricalState).toLowerCase() === key
        if (!isSavedValue) {
          const typeField = find(this.fields, {
            field_type: 'electrical_licence_type',
          })
          this.electricalTypeValue = null
          if (typeField) this.clearInformation(typeField.id)
        }
      }
    },

    updateInformation(event, id) {
      const updateIndex = findIndex(this.form.information_types, {
        id: id,
      })
      this.form.information_types[updateIndex].value = event
    },

    launchFilePicker() {
      this.$refs.filePicker.$el.childNodes[0].click()
    },

    launchSlotPicker(side) {
      const input = this.$refs[`slotInput-${side}`]
      const el = Array.isArray(input) ? input[0] : input
      if (el) el.click()
    },

    // Front/back slots (requiresBack templates): one JPG/PNG per side. The
    // upload order is always [front, back].
    onSlotPicked(side, e) {
      const list = e.dataTransfer ? e.dataTransfer.files : e.target.files
      const file = list && list[0]
      // Let the same file be picked again after a remove.
      if (e.target && !e.dataTransfer) e.target.value = ''
      if (!file) return

      const ext = (file.name || '')
        .slice((file.name || '').lastIndexOf('.') + 1)
        .toLowerCase()
      const isImage =
        ['jpg', 'jpeg', 'png'].includes(ext) ||
        ['image/jpeg', 'image/png'].includes(file.type)
      if (!isImage) {
        this.slotErrors[side] = 'Front and back must be JPG or PNG images.'
        return
      }
      if (file.size > IMAGE_MAX_BYTES) {
        this.slotErrors[side] = `Image must not be greater than ${
          IMAGE_MAX_BYTES / 1024 / 1024
        }MB.`
        return
      }

      this.slotErrors[side] = ''
      this.state.files = null
      this.errors.files = null
      const token = (this.slotTokens[side] += 1)
      this.loading = true

      const reader = new FileReader()
      reader.onerror = () => {
        this.slotErrors[side] = 'Could not read that image.'
        this.loading = false
      }
      reader.onload = (ev) => {
        const base64Img = ev.target.result
        const done = (result) => {
          if (token !== this.slotTokens[side]) return
          this.slotFiles[side] = result
          this.slotPreviews[side] = base64Img
          this.syncSlots()
          this.loading = false
        }
        if (file.size > 2000000) {
          const blob = new Blob([this.convertDataURIToBinary(base64Img)], {
            type: file.type,
          })
          // eslint-disable-next-line
          new Compressor(blob, {
            quality: 0.2,
            // See loadPhotos: the browser already applies EXIF orientation.
            checkOrientation: false,
            success: done,
            error: () => done(file),
          })
        } else {
          done(file)
        }
      }
      reader.readAsDataURL(file)
    },

    onRemoveSlot(side) {
      this.slotTokens[side] += 1
      this.slotFiles[side] = null
      this.slotPreviews[side] = null
      this.slotErrors[side] = ''
      this.syncSlots()
    },

    // Keep form.files / previews in the shape the submit and upload code expects.
    syncSlots() {
      const sides = this.cardSides.map((s) => s.key)
      this.form.files = sides.map((k) => this.slotFiles[k]).filter(Boolean)
      this.previews = sides.map((k) => this.slotPreviews[k]).filter(Boolean)
    },

    loadPhotos(e) {
      let _this = this
      const photos = e.dataTransfer ? e.dataTransfer.files : e

      this.loading = true

      // Mirrors the backend's per-type caps (app/Rules/ImageOrPdfFile.php) —
      // this is UX only, catching an oversized file before wasting time/data
      // uploading it; the server enforces the real limit regardless. PDFs
      // get more headroom than images since the largest real PDF already on
      // file predates any cap at all. CSV/XLSX stay tighter — see the
      // backend rule's docblock for why.
      const MAX_BYTES_BY_LABEL = {
        PDF: 50 * 1024 * 1024,
        Image: IMAGE_MAX_BYTES,
        CSV: 10 * 1024 * 1024,
        XLSX: 15 * 1024 * 1024,
      }

      for (let i = 0; i < photos.length; i++) {
        const file = photos[i]

        // Extension first, not MIME type — file.type is unreliable for CSV
        // specifically (Windows reports it as text/csv,
        // application/vnd.ms-excel, or sometimes an empty string depending
        // on what's set as the default handler), but the extension is
        // always there and is what the native file-picker itself filtered
        // on.
        const name = (file && file.name) || ''
        const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase()

        const isPdf = ext === 'pdf' || file.type === 'application/pdf'
        const isCsv =
          ext === 'csv' ||
          file.type === 'text/csv' ||
          file.type === 'application/vnd.ms-excel'
        const isXlsx =
          !isCsv &&
          (ext === 'xlsx' ||
            file.type ===
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        const isImage =
          !isPdf &&
          !isCsv &&
          !isXlsx &&
          (['jpg', 'jpeg', 'png'].includes(ext) ||
            file.type === 'image/jpeg' ||
            file.type === 'image/png')

        if (!isPdf && !isCsv && !isXlsx && !isImage) {
          this.state.files = false
          this.errors.files =
            'Files must be an image (JPG, PNG), PDF, CSV, or XLSX'
          this.loading = false
          continue // skip this file, don't process it
        }

        const typeLabel = isPdf
          ? 'PDF'
          : isCsv
          ? 'CSV'
          : isXlsx
          ? 'XLSX'
          : 'Image'
        const maxBytes = MAX_BYTES_BY_LABEL[typeLabel]
        if (file.size > maxBytes) {
          this.state.files = false
          this.errors.files = `${typeLabel} must not be greater than ${
            maxBytes / 1024 / 1024
          }MB.`
          this.loading = false
          continue // skip this file, don't process it
        }

        if (isPdf) {
          this.previews.push('pdf')
          this.form.files.push(file)
          this.loading = false
        } else if (isCsv) {
          this.previews.push('csv')
          this.form.files.push(file)
          this.loading = false
        } else if (isXlsx) {
          this.previews.push('xlsx')
          this.form.files.push(file)
          this.loading = false
        } else {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
            const base64Img = e.target.result
            this.previews.push(base64Img)

            if (file.size > 2000000) {
              const binaryImg = this.convertDataURIToBinary(base64Img)
              const blob = new Blob([binaryImg], { type: file.type })

              // eslint-disable-next-line
              new Compressor(blob, {
                quality: 0.2,
                // Browsers already bake EXIF orientation into the decoded
                // image, so letting Compressor rotate again squashes portrait
                // phone photos (a rotated canvas drawn with already-upright
                // pixels) — the "horizontally stretched" upload. Leave
                // orientation to the browser.
                checkOrientation: false,
                success(result) {
                  _this.addCompressedImage(result)
                },
              })
            } else {
              this.addCompressedImage(file)
            }
          }
        }
      }
    },

    addCompressedImage(result) {
      this.form.files.push(result)
      this.loading = false
    },

    convertDataURIToBinary(dataURI) {
      const BASE64_MARKER = ';base64,'
      const base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
      const base64 = dataURI.substring(base64Index)
      const raw = window.atob(base64)
      const rawLength = raw.length
      var array = new Uint8Array(new ArrayBuffer(rawLength))

      for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i)
      }
      return array
    },

    async uploadFile(photo, isLast) {
      // update form
      let data = new FormData()
      data.append('file', photo)
      await this.$api.post('media', data).then(({ data }) => {
        this.form.files.push(photo)
        this.previews.push(data.data.url)
      })

      if (isLast) {
        // enable submit if done uploading
        this.loading = false
      }
    },

    onRemovePreview(index) {
      // Previously flagged existing files in place (`.delete = true`) rather
      // than removing them, keyed on a `.delete` field the API response
      // never actually sends — so the flag never really got set, and with
      // more than one existing file the fallback index math here could flag
      // the wrong entry entirely. Splicing directly keeps form.files and
      // previews in sync (same index space, same length) regardless of how
      // many existing/new files are mixed in or removed in what order.
      const target = this.form.files[index]
      const isExistingServerFile =
        target &&
        target.id &&
        !(target instanceof File) &&
        !(target instanceof Blob)

      if (isExistingServerFile) {
        // Existing server-side file: still need to tell the API to delete
        // it — that's tracked by id in deletedFiles, independently of
        // form.files, which is what onSubmit's "at least one file remains"
        // check and the multipart upload both key off.
        this.deletedFiles.push(target.id)
      }

      this.form.files.splice(index, 1)
      this.previews.splice(index, 1)
    },

    onSelectedDate(date, selected) {
      const hasDate =
        date != null &&
        String(date).length > 0 &&
        !Number.isNaN(new Date(date).getTime())

      if (hasDate) {
        this.errors.date.field = null
        this.selectedDate = this.convertToISODateOnly(date) + 'T'
      } else {
        this.errors.date.field = selected.id
        this.selectedDate = null
      }

      this.inputDate(selected)
    },

    convertToISODateOnly(inputDate) {
      const date = new Date(inputDate)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    valueDate(date) {
      return this.new ? date : this.parseDate(date)
    },

    inputDate(selected) {
      if (this.new) {
        this.addInformation(this.selectedDate, selected.id)
      } else {
        this.updateInformation(this.selectedDate, selected.id)
      }

      if (this.calculate && !selected.label.includes('Expiry')) {
        this.calculateExpiryDate()
      }
    },

    // An optional field the user never touched has no row yet; the API still
    // expects one (sent as '') for every field on the template.
    untouchedOptionalInformation() {
      if (!this.new) return []
      return this.fields
        .filter(
          (field) =>
            field.field_type === 'electrical_conditions' &&
            !find(this.form.information_types, {
              information_type_id: field.id,
            }),
        )
        .map((field) => ({ information_type_id: field.id, value: '' }))
    },

    getFormData() {
      let formData = new FormData()
      const visibility = this.isBusinessAdminNewUploadForOwnCompany
        ? 'private'
        : this.isRequestFileCreation
        ? 'public'
        : this.form.visibility
      formData.append('name', this.form.name)
      formData.append('visibility', visibility)
      formData.append('template', this.template || '')

      localStorage.getItem('tradie_id') !== null &&
        formData.append('tradie_id', localStorage.getItem('tradie_id'))

      for (let i = 0; i < this.deletedFiles.length; i++) {
        formData.append('files[delete][' + i + ']', this.deletedFiles[i])
      }

      const rows = this.form.information_types.concat(
        this.untouchedOptionalInformation(),
      )
      for (let i = 0; i < rows.length; i++) {
        let information = rows[i]
        formData.append(
          'information_types[' + i + '][information_type_id]',
          information.information_type_id,
        )
        formData.append(
          'information_types[' + i + '][value]',
          information.value,
        )
      }
      if (this.showPrivacyConsent) {
        formData.append('privacy_consent_given', this.filePrivacyConsent)
      }

      return formData
    },

    async onSubmit() {
      // onRemovePreview() now splices removed files out of form.files (both
      // new and existing), so .length is a direct, reliable count of what's
      // actually still attached — removing every file, existing or new,
      // correctly brings this to 0.
      if (this.requiresBack && !(this.slotFiles.front && this.slotFiles.back)) {
        this.state.files = false
        this.errors.files = 'Please add both a front and back photo.'
        this.cardSides.forEach(({ key }) => {
          if (!this.slotFiles[key]) {
            this.slotErrors[key] = 'Please add this photo.'
          }
        })
        return
      }

      if (this.form.files.length === 0) {
        this.state.files = false
        this.errors.files = 'Please upload your card.'
        return
      }

      if (this.hasMissingRequiredDates) {
        this.errors.date.field = this.findFirstMissingDateFieldId()
        this.errors.date.message = 'Please input a valid date'
        return
      }

      const electricalError = this.getElectricalError()
      if (electricalError) {
        this.alertType = 'danger'
        this.alertMsg = electricalError
        this.dismissCountDown = 5
        return
      }

      this.loading = true
      const url = this.new ? 'file' : `file/${this.form.id}`

      try {
        const { data } = await this.$api.post(url, this.getFormData())
        if (data.success) {
          await this.finaliseManualUploadConsent()
          await this.uploadFiles(data.data.id)
        } else {
          this.alertType = 'danger'
          this.alertMsg = data.message || 'Could not save file.'
          this.dismissCountDown = 5
          this.loading = false
        }
      } catch (error) {
        const payload = (error && error.response && error.response.data) || {}
        this.alertType = 'danger'
        this.alertMsg =
          this.firstErrorMessage(payload) ||
          payload.message ||
          'Could not save file.'
        this.dismissCountDown = 5
        this.loading = false
      }
    },

    // First message of a 422 `errors` object, which is more useful than the
    // generic "The given data was invalid."
    firstErrorMessage(payload) {
      const errors = payload && payload.errors
      if (!errors || typeof errors !== 'object') return ''
      const first = Object.values(errors)[0]
      const message = Array.isArray(first) ? first[0] : first
      return typeof message === 'string' ? message : ''
    },

    // Disabled controls skip native `required` validation, so the licence type
    // and restriction length are checked here as well.
    getElectricalError() {
      if (this.hasElectricalType && this.hasStateField) {
        if (!this.electricalTypeValue) return 'Select a licence type.'
      }
      if (this.electricalRestrictionsTooLong) {
        return this.ELECTRICAL_RESTRICTIONS_MESSAGE
      }
      return ''
    },

    getFileData() {
      let formData = new FormData()
      this.form.files
        .filter((item) => item instanceof File || item instanceof Blob)
        .forEach((file) => formData.append('files[]', file))
      return formData
    },

    /**
     * Record the tradie's manual-upload consent against their account once the
     * file has saved. Best-effort — the file is already stored, so a failure
     * here must not block the upload finishing.
     */
    async finaliseManualUploadConsent() {
      if (!this.showManualUploadConsent || !this.manualUploadConsent) return
      try {
        await this.acceptConsent('manual_upload')
      } catch (e) {
        // non-blocking — see above
      }
    },

    async uploadFiles(file) {
      const newFiles = this.form.files.filter(
        (item) => item instanceof File || item instanceof Blob,
      )

      if (newFiles.length === 0) {
        await this.successUpload(file)
        this.loading = false
        return
      }

      const formData = new FormData()
      newFiles.forEach((f, i) => {
        let toAppend = f
        const isFile = f instanceof File && f.name
        const hasValidExt =
          isFile && /\.(jpe?g|png|pdf|csv|xlsx)$/i.test(f.name)

        if (!hasValidExt) {
          const ext =
            f.type === 'image/png'
              ? 'png'
              : f.type === 'application/pdf'
              ? 'pdf'
              : f.type === 'text/csv' || f.type === 'application/vnd.ms-excel'
              ? 'csv'
              : f.type ===
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              ? 'xlsx'
              : 'jpg'
          toAppend = new File([f], `file-${i}.${ext}`, {
            type: f.type || 'image/jpeg',
          })
        } else {
          // Sanitize the filename — replace spaces, parentheses and other
          // special characters that break server-side thumbnail generation
          const safeName = f.name.replace(/[^a-zA-Z0-9.\-_]/g, '_')
          toAppend = new File([f], safeName, { type: f.type })
        }

        formData.append('files[]', toAppend)
      })

      await this.$api
        .post(`upload-file/${file}`, formData)
        .then(({ data }) => {
          if (data.success) {
            this.successUpload(file)
          }
        })
        .catch((error) => {
          const payload = (error && error.response && error.response.data) || {}
          const message =
            payload.message || 'Something went wrong while uploading.'
          this.alertType = 'danger'
          this.alertMsg = message
          this.dismissCountDown = 5
        })
      this.loading = false
    },

    async successUpload(file) {
      if (this.highlightExpiry) {
        // Renewal-request flow (see EditFilePage/NotificationsList) — the
        // tradie came from a notification, not from browsing their files,
        // so send them back to the dashboard instead of a file view/list
        // they weren't navigating from.
        this.$router.replace({ name: 'home.welcome-page' })
        return
      }

      if (this.isRequestFileCreation) {
        this.$router.replace({
          name: 'request.view-file-page',
          params: { id: file },
        })

        // let formData = {
        //   file: file,
        //   fileRequestId: this.request.id,
        // }

        // await this.fileRequestShare(formData)
        //   .then((res) => {
        //     this.$router.replace({
        //       name: 'file.success-page',
        //       params: {
        //         id: file,
        //       },
        //     })
        //   })
        //   .catch((error) => {
        //     if (error.status === 402) {
        //       this.$api.delete(`file/${file}`).then(({ data }) => {
        //         if (data.success) {
        //           this.alertType = 'danger'
        //           this.dismissCountDown = 15
        //           this.alertMsg = error.data.message
        //         }
        //       })
        //     }
        //   })
      } else {
        const { data } = await this.$api.get(`file/${file}`)
        const fileData = data.data

        const projectId = localStorage.getItem('project_id')
        const tradieId = localStorage.getItem('tradie_id')

        if (fileData.user && fileData.user.id === this.user.id) {
          this.$router.push({ name: 'file.list-page' })
          return
        }

        if (projectId && tradieId) {
          this.$router.replace({
            name: 'file.view-page',
            params: { id: file },
          })
          return
        }

        if (this.new) {
          this.$router.replace({
            name: 'file.success-page',
            params: {
              id: file,
            },
          })
        } else {
          this.$router.replace({
            name: 'file.view-page',
            params: {
              id: file,
            },
          })
        }
      }
    },

    resetForm() {
      if (!this.new) {
        // update form
        for (let key in this.form) {
          this.form[key] = this.file[key]
          this.form.id = this.file.id
          this.previews = this.file.files.map((p) => p.thumb_url)
          this.form.information_types = this.file.file_information_types.map(
            (info) => ({
              ...info,
              information_type_id: info.information_type_id,
            }),
          )
          this.form.files = this.file.files
        }
        if (!this.form.visibility) {
          this.form.visibility = 'public'
        }
      }
      // A BA uploading for a tradie starts Private so a wrong upload can be deleted before the tradie sees it.
      if (this.new && this.isBusinessAdminUploadingForTradie) {
        this.form.visibility = 'private'
      }
      // A file uploaded to answer a company's file request must be Public, or
      // the company that asked for it can't see the response.
      if (this.isRequestFileCreation) {
        this.form.visibility = 'public'
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    },

    calculateExpiryDate() {
      const fromIndex = findIndex(this.form.information_types, {
        information_type_id: this.hasSwitch.base_information_type_id,
      })
      const toIndex = findIndex(this.form.information_types, {
        information_type_id: this.hasSwitch.target_information_type_id,
      })
      if (fromIndex >= 0) {
        const addedDate = dayjs(this.selectedDate).add(
          this.calculateYears,
          'year',
        )

        // prettier-ignore
        const index = findIndex(this.fields, { id: this.hasSwitch.target_information_type_id })
        this.addedDate = this.convertToISODateOnly(addedDate) + 'T'
        this.$set(this.fields[index], 'value', this.addedDate)

        if (toIndex >= 0) {
          this.form.information_types[toIndex].value = this.addedDate
        } else {
          this.form.information_types.push({
            information_type_id: this.hasSwitch.target_information_type_id,
            value: this.addedDate,
          })
        }
      }
    },

    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    onPickingOption(optionType, status) {
      this.isPickingOptions = status

      switch (optionType) {
        case 'high_risk_work_class':
          this.isPickingClass = status
          break

        case 'traffic_controller_qualification':
          this.isPickingQualification = status
          break

        case 'driver_licence_class':
          this.isPickingDriverLicenceClasses = status
          break
        case 'electrical_restriction':
          this.isPickingElectricalRestrictions = status
          break
      }
    },

    onOptionsPicked(optionType, data, id) {
      if (
        optionType === 'electrical_restriction' &&
        data.options.length > this.ELECTRICAL_RESTRICTIONS_MAX
      ) {
        this.showElectricalRestrictionsTooLong()
        return
      }
      this.onPickingOption(false)
      this.addInformation(data.options, id)

      switch (optionType) {
        case 'high_risk_work_class':
          this.classes = data.options
          break

        case 'traffic_controller_qualification':
          this.qualifications = data.options
          break

        case 'driver_licence_class':
          this.driverLicenceClasses = data.options
          this.form.name = `Driver Licence ${data.options}`
          break
        case 'electrical_restriction':
          this.electricalRestrictions = data.options
          break
      }
    },

    // Edit mode: seed the picker state synchronously from the saved file so a
    // failing fetch later cannot leave the fields blank.
    seedElectricalFromFile() {
      if (this.new) return
      const infoTypes = this.file.file_information_types || []
      const savedState = infoTypes.find((x) => x.field_type === 'state')
      const savedType = infoTypes.find(
        (x) => x.field_type === 'electrical_licence_type',
      )
      const savedRestrictions = infoTypes.find(
        (x) => x.field_type === 'electrical_restrictions',
      )
      if (savedType) {
        this.electricalState = (savedState && savedState.value) || ''
        this.savedElectricalState = this.electricalState
        this.savedElectricalType = savedType.value || null
        this.electricalTypeValue = this.savedElectricalType
      }
      if (savedRestrictions) {
        this.electricalRestrictions = savedRestrictions.value || ''
      }
    },

    showElectricalRestrictionsTooLong() {
      this.alertType = 'danger'
      this.alertMsg = this.ELECTRICAL_RESTRICTIONS_MESSAGE
      this.dismissCountDown = 5
    },
  },

  async mounted() {
    // Electrical licence (edit mode): the saved values were seeded in
    // created(); only the (self-handling) fetch happens here, first, so a
    // failing request below cannot skip it.
    if (!this.new && this.savedElectricalType) {
      this.loadElectricalTypes(this.electricalState)
    }
    await this.getCountries()
    await this.getOptionValues('state').then(() => {
      this.options.sort((a, b) => (a.order_no < b.order_no ? -1 : 1))
      this.states = this.options
    })
    this.form.name = this.file.name

    if (this.selectedFields === null) {
      // Qualifications
      const matchQualifications = this.file.file_information_types.find(
        (x) => x.field_type === 'traffic_controller_qualifications',
      )
      if (matchQualifications) {
        this.qualifications = matchQualifications.value
      }

      // Classes
      const matchClasses = this.file.file_information_types.find(
        (x) => x.field_type === 'classes',
      )
      if (matchClasses) {
        this.classes = matchClasses.value
      }

      // States
      const matchStates = this.file.file_information_types.find(
        (x) => x.field_type === 'state',
      )
      if (matchStates) {
        this.classes = matchStates.value
      }

      // Driver Licence Class(es)
      const matchDriverLicenceClasses = this.file.file_information_types.find(
        (x) => x.field_type === 'driver_licence_classes',
      )
      if (matchDriverLicenceClasses) {
        this.driverLicenceClasses = matchDriverLicenceClasses.value
      }
    }
  },

  created() {
    this.resetForm()
    this.seedElectricalFromFile()

    // if (this.new) {
    //   // create form
    //   this.uploadType =
    //     this.file.uploadType === 'single' ? 'Single File' : 'Multiple Files'
    // } else {
    //   // update form
    //   this.uploadType =
    //     this.file.files.length > 1 ? 'Multiple Files' : 'Single File'
    // }
  },
}
</script>

<style lang="scss" scoped>
.btn {
  height: 56px;
}

.remove-icon {
  position: absolute;
  right: 6px;
  top: 6px;
  cursor: pointer;
}

// The shared .upload-container shrink-wraps to its content (max-content), so
// size the slots explicitly: equal boxes, centred in their half of the row.
.card-slot {
  position: relative;
  width: 100%;
  max-width: 20rem;
  height: 16rem;
  max-height: none;
  margin: 0 auto;
  align-items: center;
  overflow: hidden;
  text-align: center;
  &.is-invalid {
    border-color: #dc3545;
  }
}

// A card photo is full size (often 800px+), so cap it inside the slot.
.slot-preview {
  display: block;
  width: calc(100% - 2rem);
  height: 9rem;
  margin: 1rem auto 0;
  object-fit: contain;
}

.preview-item {
  width: 180px;
  height: 180px;
}

.upload-border {
  width: 100%;
  max-width: inherit;
}

.input-files-feedback {
  background: #ffe2e2 0% 0% no-repeat padding-box;
  color: red;
  text-align: center;
}

.visibility-card {
  border: 0;
  border-radius: 0;
  padding: 0;
  background: transparent;
  box-shadow: none;
}

/deep/ .visibility-toggle-wrap {
  max-width: 460px;
}

/deep/ .visibility-toggle.btn-group-toggle {
  display: flex;
  width: 100%;
  border: 1px solid #dfe3eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f7f9fc;
}

/deep/ .visibility-toggle .btn {
  flex: 1;
  margin: 0;
  border: 0;
  height: 36px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #4b5563;
  background: transparent;
}

/deep/ .visibility-toggle .btn.active {
  background: #1677ff;
  color: #fff;
  box-shadow: none;
}

/deep/ .visibility-toggle .btn:not(.active):hover {
  background: #edf2f7;
}

.visibility-help-title {
  display: inline;
  font-weight: 500;
  color: #334155;
  font-size: 0.88rem;
  line-height: 1.35;
  margin-bottom: 0;
}

.visibility-mode-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #0f3f8a;
  background: #e9f1ff;
  border: 1px solid #cfe0ff;
  vertical-align: middle;
}

.custom-label {
  min-width: 100vw;
  font-size: 1rem;

  @media (max-width: 767px) {
    // No viewport-based width here: 75vw pushed the text past the card's inner
    // edge. Let it size to the container and wrap naturally instead.
    min-width: 0;
    max-width: 100%;
    font-size: 0.875rem;
    padding-left: 0.5rem !important; // tighter than pl-3 next to the smaller switch
    // A <label>'s default margin-bottom skews the flex centering — the text box
    // gets centered including the margin, lifting the visible text above the
    // toggle. Zero it so the text truly centers.
    margin-bottom: 0;
  }

  // Once the two date fields sit inline (≥576px) there's room for the whole
  // sentence on one line — stop it wrapping at its column's edge, and use the
  // full desktop text size to match the full-size switch.
  @media (min-width: 576px) and (max-width: 767px) {
    max-width: none;
    white-space: nowrap;
    font-size: 1rem;
  }
}

// Dates inline (576–767.98px): keep the theme's full-size 56x32 track but fix
// its geometry — the global left offset (-45px wrapper padding) lets the track
// underlap the text, so widen the inset and vertically center everything.
@media (min-width: 576px) and (max-width: 767.98px) {
  /deep/ .custom-switch.b-custom-control-lg {
    padding-left: 66px !important;

    .custom-control-label {
      display: flex !important;
      align-items: center;
      min-height: 32px;
      padding-top: 0 !important;
    }

    .custom-control-label::before {
      top: 50% !important;
      transform: translateY(-50%);
      left: -66px !important;
    }

    .custom-control-label::after {
      top: 50% !important;
      transform: translateY(-50%);
      left: -64px !important;
    }

    .custom-control-input:checked ~ .custom-control-label::after {
      left: -64px !important;
      transform: translateY(-50%) translateX(24px) !important;
    }
  }
}

// Dates stacked (<576px): shrink the expiry-date switch. The global theme
// (main.scss .custom-switch rules) hardcodes a 56x32px track with !important,
// so these must be !important too or they lose the cascade. Desktop keeps the
// global size.
@media (max-width: 575.98px) {
  /deep/ .custom-switch.b-custom-control-lg {
    padding-left: 52px !important;

    // Keep the toggle and text on one horizontal line, vertically centered
    // against each other even when the text wraps to two lines.
    .custom-control-label {
      display: flex !important;
      align-items: center;
      min-height: 28px;
      padding-top: 0 !important;
    }

    .custom-control-label::before {
      top: 50% !important;
      transform: translateY(-50%);
      left: -52px !important;
      width: 44px !important;
      height: 24px !important;
      border-radius: 12px !important;
    }

    .custom-control-label::after {
      top: 50% !important;
      transform: translateY(-50%);
      left: -50px !important;
      width: 20px !important;
      height: 20px !important;
      border-radius: 10px !important;
    }

    .custom-control-input:checked ~ .custom-control-label::after {
      // The global checked rule repositions via `left`; pin both left and
      // transform so the knob lands inside the shrunken track (translateY
      // repeated so the vertical centering survives the checked state).
      left: -50px !important;
      transform: translateY(-50%) translateX(20px) !important;
    }
  }
}

/deep/ .datepicker > label {
  align-content: center;
}

// Draws the eye to the Expiry Date field when this edit was opened from a
// renewal-request notification (see FileForm's highlightExpiry prop) —
// padding/margin match the plain .mb-3 wrapper it's added alongside so
// nothing else in the layout shifts.
.highlight-expiry {
  padding: 0.6rem 0.75rem 0.5rem;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
  background: #fff8e6;
  border: 1px solid #f5c451;
  border-radius: 6px;

  /deep/ .datepicker {
    border-radius: 4px;
    box-shadow: 0 0 0 1px #f5c451;
  }
}

.highlight-expiry-badge {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.05rem 0.5rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #7a5200;
  background: #ffe6a3;
  border-radius: 999px;
  vertical-align: middle;
}

.consent-box {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.consent-notice-text {
  white-space: pre-line;
  max-height: 220px;
  overflow-y: auto;
}
</style>
